import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box } from '@mui/material';
import {
  HeroSection,
  AboutSection,
  ServicesSection,
  PortfolioSection,
  TestimonialsSection,
  ContactSection,
} from './sections';
import Calculator from '../../components/Calculator';
import FAQSection from './sections/FAQSection';

const Home: React.FC = () => {
  return (
    <Box component="main">
      <Helmet>
        <title>Webseite erstellen in Darmstadt | Professionelle Website-Agentur | pyzon</title>
        <meta name="description" content="Ihre professionelle Website-Agentur in Darmstadt. Wir erstellen moderne, benutzerfreundliche und SEO-optimierte Webseiten für Ihr Unternehmen. Responsive Design, E-Commerce und individuelle Lösungen." />
        <meta name="keywords" content="Webseite erstellen, Website Agentur Darmstadt, Homepage erstellen, Webentwicklung, Website verbessern, Responsive Design, SEO Optimierung, Website Agentur, pyzon" />
        
        {/* Open Graph / Facebook Meta Tags für Social Media Sharing */}
        <meta property="og:title" content="Webseite erstellen in Darmstadt | Professionelle Website-Agentur | pyzon" />
        <meta property="og:description" content="Ihre professionelle Website-Agentur in Darmstadt. Wir erstellen moderne, benutzerfreundliche und SEO-optimierte Webseiten für Ihr Unternehmen. Responsive Design, E-Commerce und individuelle Lösungen." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pyzon.de" />
        <meta property="og:image" content="https://pyzon.de/logo.svg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="pyzon Website-Agentur" />
        <meta property="og:locale" content="de_DE" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Webseite erstellen in Darmstadt | Professionelle Website-Agentur | pyzon" />
        <meta name="twitter:description" content="Ihre professionelle Website-Agentur in Darmstadt. Wir erstellen moderne, benutzerfreundliche und SEO-optimierte Webseiten für Ihr Unternehmen." />
        <meta name="twitter:image" content="https://pyzon.de/logo.svg" />
        
        <link rel="canonical" href="https://pyzon.de" />
        
        {/* Strukturierte Daten für Google Search */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "pyzon Website-Agentur",
              "description": "Professionelle Website-Agentur in Darmstadt",
              "url": "https://pyzon.de",
              "logo": "https://pyzon.de/logo.svg",
              "image": "https://pyzon.de/logo.svg",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Soderstraße 51",
                "addressLocality": "Darmstadt",
                "postalCode": "64287",
                "addressRegion": "Hessen",
                "addressCountry": "DE"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": "49.8728",
                "longitude": "8.6512"
              },
              "priceRange": "€€",
              "telephone": "+4915205341897",
              "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday"
                ],
                "opens": "09:00",
                "closes": "18:00"
              },
              "sameAs": [
                "https://github.com/phpyzon",
                "https://www.linkedin.com/in/patrick-hutter-415078150/"
              ],
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://pyzon.de/search?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              }
            }
          `}
        </script>
        
        {/* Strukturierte Daten für Sitelinks */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "url": "https://pyzon.de/",
              "name": "pyzon Website-Agentur",
              "description": "Professionelle Website-Agentur in Darmstadt",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://pyzon.de/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          `}
        </script>
        
        {/* Strukturierte Daten für Sitelinks im Suchergebnis */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "SiteNavigationElement",
                  "position": 1,
                  "name": "Über uns",
                  "description": "Erfahren Sie mehr über pyzon Website-Agentur in Darmstadt",
                  "url": "https://pyzon.de/about"
                },
                {
                  "@type": "SiteNavigationElement",
                  "position": 2,
                  "name": "Services",
                  "description": "Unsere Website und Webentwicklung Dienstleistungen",
                  "url": "https://pyzon.de/services"
                },
                {
                  "@type": "SiteNavigationElement",
                  "position": 3,
                  "name": "Portfolio",
                  "description": "Unsere Website Projekte und Referenzen",
                  "url": "https://pyzon.de/portfolio"
                },
                {
                  "@type": "SiteNavigationElement",
                  "position": 4,
                  "name": "Kontakt",
                  "description": "Kontaktieren Sie uns für Ihre Webseite",
                  "url": "https://pyzon.de/contact"
                }
              ]
            }
          `}
        </script>
      </Helmet>

      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <PortfolioSection />
      <Calculator />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />
    </Box>
  );
};

export default Home;
